import React, { useState } from 'react';
import { Loader2, Send, PackageSearch, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { contactAPI } from '../services/api';
import { vehicleStore } from '../services/vehicleStore';
import { useT } from '../services/i18n';

export const PART_REQUEST_SUBJECT = 'Demande de pièce introuvable';

/** « Demander cette pièce » — sends a contact ticket pre-filled with the search and the selected vehicle. */
const PartRequestDialog = ({ open, onOpenChange, query, vehicle }) => {
  const { t } = useT();
  const [form, setForm] = useState({ name: '', email: '', phone: '', details: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const vehicleLabel = vehicleStore.label(vehicle);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const message = [`${t('partRequest.msgSearch')} : ${query}`, vehicleLabel ? `${t('partRequest.msgVehicle')} : ${vehicleLabel}` : '', form.details.trim() ? `${t('partRequest.msgDetails')} : ${form.details.trim()}` : ''].filter(Boolean).join('\n');
      const res = await contactAPI.send({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), subject: PART_REQUEST_SUBJECT, message, page_url: window.location.href, source: 'formulaire', part_query: query, vehicle: vehicleLabel });
      setDone(res.ticket || true);
      toast.success(t('partRequest.sent'));
    } catch (err) { toast.error(err.response?.data?.detail || t('contact.failed')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="part-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PackageSearch size={20} className="text-orange-500" /> {t('partRequest.title')}</DialogTitle>
          <DialogDescription>{t('partRequest.intro')}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-6" data-testid="part-request-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <p className="font-semibold text-gray-900">{t('partRequest.sent')}</p>
            <p className="text-sm text-gray-600 mt-1">{t('partRequest.sentDesc')}{typeof done === 'string' ? ` (${done})` : ''}</p>
            <Button className="mt-4" variant="outline" onClick={() => onOpenChange(false)} data-testid="part-request-close">{t('common.close')}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="rounded-md bg-orange-50 border border-orange-100 px-3 py-2 text-sm text-gray-800" data-testid="part-request-summary">
              <p><span className="text-gray-500">{t('partRequest.msgSearch')} :</span> <strong>{query}</strong></p>
              {vehicleLabel && <p><span className="text-gray-500">{t('partRequest.msgVehicle')} :</span> {vehicleLabel}</p>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input required minLength={2} placeholder={t('contact.fullName')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="part-request-name" />
              <Input required type="email" placeholder={t('chat.emailPh')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="part-request-email" />
            </div>
            <Input placeholder={t('contact.phone') + ' (optionnel)'} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="part-request-phone" />
            <textarea rows={3} placeholder={t('partRequest.detailsPh')} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="part-request-details"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
            <p className="text-[11px] text-gray-400">{t('contact.privacyNote')}</p>
            <Button type="submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="part-request-submit">
              {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />} {t('partRequest.submit')}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PartRequestDialog;
